import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_SID = 'analytics_sid';
const HEARTBEAT_INTERVAL = 30 * 1000; // 30 s
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 min

// Returns true if user accepted analytics/marketing cookies. Essential-only -> false.
const hasAnalyticsConsent = () => {
  try {
    const raw = localStorage.getItem('cookie_consent');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.type === 'all';
  } catch {
    return false;
  }
};

// Skip tracking for admin pages and logged-in admins.
const isAdminContext = (pathname) => {
  if (!pathname) pathname = window.location.pathname;
  if (pathname.startsWith('/admin')) return true;
  try {
    if (localStorage.getItem('admin_token')) return true;
  } catch {}
  return false;
};

const detectDeviceType = () => {
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
};

const getSessionId = () => {
  try {
    const existing = localStorage.getItem(STORAGE_SID);
    if (existing) {
      const [id, lastTs] = existing.split('::');
      // If last activity < 30 min ago, reuse session. Else new id.
      if (id && lastTs && Date.now() - parseInt(lastTs, 10) < SESSION_TIMEOUT) {
        return id;
      }
    }
  } catch {}
  const id = 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  localStorage.setItem(STORAGE_SID, `${id}::${Date.now()}`);
  return id;
};

const touchSession = (sid) => {
  try { localStorage.setItem(STORAGE_SID, `${sid}::${Date.now()}`); } catch {}
};

const parseUtm = (search) => {
  const p = new URLSearchParams(search || '');
  return {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
  };
};

const post = (payload) => {
  // Use beacon when possible (works on page unload)
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/analytics/event`, blob);
      return;
    }
  } catch {}
  axios.post(`${API}/analytics/event`, payload).catch(() => {});
};

// Public helper: track custom click events (e.g. partner logo, outbound link).
// Respects consent and admin filtering.
// Usage: trackClick({ label: 'Partner Opel', url: 'https://opel.cz', kind: 'partner' });
export const trackClick = ({ label, url, kind = 'click', meta = {} } = {}) => {
  try {
    if (!hasAnalyticsConsent()) return;
    if (isAdminContext(window.location.pathname)) return;
    let sid = '';
    try {
      const existing = localStorage.getItem(STORAGE_SID);
      if (existing) sid = existing.split('::')[0] || '';
    } catch {}
    if (!sid) {
      sid = 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      try { localStorage.setItem(STORAGE_SID, `${sid}::${Date.now()}`); } catch {}
    }
    post({
      session_id: sid,
      path: window.location.pathname + (window.location.search || ''),
      referrer: document.referrer || '',
      device_type: (window.innerWidth < 640 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'),
      language: navigator.language || '',
      event_type: 'click',
      click_kind: kind,  // 'partner' | 'link' | 'button' | 'social' | ...
      click_label: String(label || '').slice(0, 200),
      click_url: String(url || '').slice(0, 500),
      click_meta: meta,
      duration_ms: 0,
    });
  } catch {}
};

/**
 * AnalyticsTracker — mount once at app root.
 *  - No-op unless visitor accepted analytics cookies.
 *  - Sends a `view` event on every route change.
 *  - Sends a `heartbeat` every 30 s while tab is active.
 *  - Sends a `leave` event on pagehide/unload with total duration.
 */
const AnalyticsTracker = () => {
  const location = useLocation();
  const lastViewAt = useRef(Date.now());
  const hbTimer = useRef(null);
  const sidRef = useRef(null);
  // consent state — updates when cookie bar user clicks accept (same-tab event)
  const [consent, setConsent] = useState(() => hasAnalyticsConsent());

  // Listen for consent changes (cookie bar fires 'cookie-consent-updated' after accept)
  useEffect(() => {
    const refreshConsent = () => setConsent(hasAnalyticsConsent());
    window.addEventListener('cookie-consent-updated', refreshConsent);
    window.addEventListener('storage', refreshConsent);
    return () => {
      window.removeEventListener('cookie-consent-updated', refreshConsent);
      window.removeEventListener('storage', refreshConsent);
    };
  }, []);

  useEffect(() => {
    if (!consent) return;
    if (isAdminContext(location.pathname)) return;
    const sid = getSessionId();
    sidRef.current = sid;

    // Fire `view` event
    const utm = parseUtm(window.location.search);
    post({
      session_id: sid,
      path: location.pathname + (location.search || ''),
      referrer: document.referrer || '',
      device_type: detectDeviceType(),
      language: navigator.language || '',
      screen_w: window.screen?.width || 0,
      screen_h: window.screen?.height || 0,
      event_type: 'view',
      duration_ms: 0,
      ...utm,
    });
    lastViewAt.current = Date.now();
    touchSession(sid);
  }, [location.pathname, location.search, consent]);

  useEffect(() => {
    if (!consent) return;
    if (isAdminContext(window.location.pathname)) return;
    const sid = sidRef.current || getSessionId();
    sidRef.current = sid;

    // Heartbeat
    const sendHeartbeat = () => {
      if (document.visibilityState !== 'visible') return;
      if (isAdminContext(window.location.pathname)) return;
      post({
        session_id: sid,
        path: window.location.pathname,
        referrer: '',
        device_type: detectDeviceType(),
        language: navigator.language || '',
        event_type: 'heartbeat',
        duration_ms: HEARTBEAT_INTERVAL,
      });
      touchSession(sid);
    };
    hbTimer.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Leave event
    const onLeave = () => {
      if (isAdminContext(window.location.pathname)) return;
      const duration = Date.now() - lastViewAt.current;
      post({
        session_id: sid,
        path: window.location.pathname,
        referrer: '',
        device_type: detectDeviceType(),
        language: navigator.language || '',
        event_type: 'leave',
        duration_ms: Math.min(duration, 3600 * 1000),
      });
    };
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('beforeunload', onLeave);

    // Global outbound-link click tracking (delegated)
    const onDocClick = (e) => {
      const a = e.target?.closest?.('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
      let destHost = '';
      try { destHost = new URL(href, window.location.href).host; } catch { return; }
      if (!destHost || destHost === window.location.host) return;  // internal
      // Skip if explicitly marked
      if (a.dataset.noTrack === '1') return;
      // Determine kind based on known partner-slider parent
      const isPartner = !!a.closest('[data-testid="partners-slider"], [data-testid="partners-block"]');
      if (isPartner) return;  // already tracked explicitly
      // Figure out label
      const label = (a.textContent || '').trim().slice(0, 120) || a.getAttribute('title') || a.querySelector('img')?.getAttribute('alt') || destHost;
      const kind = /(facebook|instagram|twitter|x\.com|linkedin|youtube|tiktok|messenger)/i.test(destHost) ? 'social'
                 : href.startsWith('mailto:') ? 'email'
                 : href.startsWith('tel:') ? 'phone'
                 : 'outbound';
      trackClick({ kind, label, url: href });
    };
    document.addEventListener('click', onDocClick, true);

    return () => {
      if (hbTimer.current) clearInterval(hbTimer.current);
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('beforeunload', onLeave);
      document.removeEventListener('click', onDocClick, true);
    };
  }, [consent]);

  return null;
};

export default AnalyticsTracker;
